import { useToast } from '@/components/ui/toast'
import type { Document, ImageproxyImagePreset } from '@/types/generated'
import type { PageItem, DocumentGroup } from './useDocumentState'
import { uniqueZipFilename } from '@/lib/zipFilenames'
import { CMAP_URL, CMAP_PACKED } from '@/utils/pdfjs'

export const usePdfOperations = (t: (key: string, params?: Record<string, any>) => string) => {
  const { toast } = useToast()

  const createAndDownloadZip = async (zip: any, filename: string): Promise<void> => {
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const url = window.URL.createObjectURL(zipBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const fetchDocumentAsBlob = async (url: string, filename: string): Promise<Blob> => {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}`)
    }
    return response.blob()
  }

  const fetchDocumentAsArrayBuffer = async (
    url: string,
    filename: string
  ): Promise<ArrayBuffer> => {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}`)
    }
    return response.arrayBuffer()
  }

  const getActualPageCount = async (pdfUrl: string): Promise<number> => {
    try {
      // Dynamically import PDF.js only when needed
      const pdfjsLib = await import('pdfjs-dist')
      const pdfLib = pdfjsLib.default || pdfjsLib

      if (!pdfLib.GlobalWorkerOptions?.workerSrc) {
        pdfLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfLib.version}/build/pdf.worker.min.mjs`
      }

      const loadingTask = pdfLib.getDocument({
        url: pdfUrl,
        cMapUrl: CMAP_URL,
        cMapPacked: CMAP_PACKED
      })
      const pdf = await loadingTask.promise
      const pageCount = pdf.numPages

      // Clean up
      loadingTask.destroy()

      return pageCount
    } catch (error) {
      console.warn('Failed to get actual page count from PDF:', error)
      throw error
    }
  }

  const validateDocumentPageCount = async (
    document: Document,
    fallbackPageCount: number = 1
  ): Promise<number> => {
    let pageCount = document.page_count || fallbackPageCount

    // If page_count seems unreliable (0 or 1 for a document that should have more),
    // try to validate it by checking the PDF content
    if (pageCount <= 1 && document.url) {
      try {
        const actualPageCount = await getActualPageCount(document.url)
        if (actualPageCount > 1) {
          pageCount = actualPageCount
        }
      } catch (error) {
        console.warn(`Could not validate page count for document ${document.filename}:`, error)
        // Keep the original page_count if validation fails
      }
    }

    // Ensure page count is at least 1
    return Math.max(1, pageCount)
  }

  const createDocumentPages = async (
    document: Document,
    documentIndex: number,
    thumbnails: ImageproxyImagePreset[] = []
  ): Promise<PageItem[]> => {
    const pageCount = await validateDocumentPageCount(document)
    const pages: PageItem[] = []

    for (let i = 0; i < pageCount; i++) {
      const page: PageItem = {
        uuid: `${document.uuid}-page-${i + 1}`,
        filename: document.filename || `Document ${documentIndex + 1}`,
        images: document.images || [],
        thumbnails: thumbnails, // Add thumbnails array
        pageNumber: i + 1,
        actualPageNumber: i + 1,
        documentUuid: document.uuid,
        url: document.url
      }
      pages.push(page)
    }

    return pages
  }

  const createPdfFromPages = async (
    pages: PageItem[],
    pageRotations?: Map<string, number> | Record<string, number>,
    onProgress?: (progress: number) => void
  ): Promise<Uint8Array> => {
    const { PDFDocument, degrees } = await import('pdf-lib')
    const newPdfDoc = await PDFDocument.create()

    // Group pages by their source document URL to avoid re-fetching
    const pagesByUrl = new Map<string, { pages: PageItem[]; arrayBuffer?: ArrayBuffer }>()

    pages.forEach((page) => {
      if (!pagesByUrl.has(page.url)) {
        pagesByUrl.set(page.url, { pages: [] })
      }
      pagesByUrl.get(page.url)!.pages.push(page)
    })

    // Fetch all unique documents in parallel
    const urlsToFetch = Array.from(pagesByUrl.keys())
    console.log(`Fetching ${urlsToFetch.length} unique documents for ${pages.length} pages`)

    const fetchPromises = urlsToFetch.map(async (url) => {
      try {
        const firstPage = pagesByUrl.get(url)!.pages[0]
        const arrayBuffer = await fetchDocumentAsArrayBuffer(url, firstPage.filename)
        pagesByUrl.get(url)!.arrayBuffer = arrayBuffer
        return { url, success: true }
      } catch (error) {
        console.error(`Error fetching document ${url}:`, error)
        return { url, success: false, error }
      }
    })

    // Wait for all documents to be fetched
    const fetchResults = await Promise.all(fetchPromises)
    const successfulFetches = fetchResults.filter((result) => result.success)
    console.log(`Successfully fetched ${successfulFetches.length}/${urlsToFetch.length} documents`)

    // Process pages in order
    let processedPages = 0
    for (const page of pages) {
      try {
        const documentGroup = pagesByUrl.get(page.url)
        if (!documentGroup?.arrayBuffer) {
          console.warn(`Skipping page ${page.filename} - document not available`)
          continue
        }

        const sourcePdfDoc = await PDFDocument.load(documentGroup.arrayBuffer)
        const actualPageNumber = (page.actualPageNumber || page.pageNumber) - 1

        if (actualPageNumber >= 0 && actualPageNumber < sourcePdfDoc.getPageCount()) {
          const [copiedPage] = await newPdfDoc.copyPages(sourcePdfDoc, [actualPageNumber])

          // Apply rotation if specified
          if (pageRotations) {
            // Handle both Map and Object types for pageRotations
            let rotation = 0
            if (pageRotations instanceof Map) {
              rotation = pageRotations.get(page.uuid) || 0
            } else if (typeof pageRotations === 'object' && pageRotations !== null) {
              rotation = (pageRotations as any)[page.uuid] || 0
            }

            if (rotation !== 0) {
              const currentRotation = copiedPage.getRotation().angle || 0
              const newRotation = (currentRotation + rotation) % 360
              const pdfRotation = degrees(newRotation)
              copiedPage.setRotation(pdfRotation)
            }
          }

          newPdfDoc.addPage(copiedPage)
        }

        processedPages++
        if (onProgress) {
          onProgress((processedPages / pages.length) * 100)
        }
      } catch (error) {
        console.error(`Error processing page ${page.filename}:`, error)
        processedPages++
        if (onProgress) {
          onProgress((processedPages / pages.length) * 100)
        }
        // Continue with other pages even if one fails
      }
    }

    console.log(`Created PDF with ${newPdfDoc.getPageCount()} pages`)
    return await newPdfDoc.save()
  }

  /**
   * Download original PDFs as they were uploaded
   */
  const downloadOriginalPdfs = async (
    documents: Document[],
    organizedNames?: Record<string, string>
  ) => {
    try {
      const loadingToast = toast({
        title: t('downloading'),
        description: t('downloadingOriginalPdfs'),
        variant: 'default'
      })

      const JSZip = (await import('jszip')).default
      const zip = new JSZip()
      const usedNames = new Set<string>()
      const failed: string[] = []

      for (const doc of documents) {
        const desiredName = organizedNames?.[doc.uuid] || doc.filename || `document_${doc.uuid}.pdf`
        try {
          const blob = await fetchDocumentAsBlob(doc.url, doc.filename)
          zip.file(uniqueZipFilename(desiredName, usedNames), blob)
        } catch (error) {
          console.error(`Error downloading ${doc.filename}:`, error)
          failed.push(desiredName)
        }
      }

      const successCount = documents.length - failed.length
      loadingToast.dismiss()

      if (successCount === 0) {
        toast({
          title: t('downloadAllFailed'),
          description: t('downloadAllFailedDescription', { totalCount: documents.length }),
          variant: 'destructive'
        })
        return
      }

      const filename = `original_documents_${new Date().toISOString().split('T')[0]}.zip`
      await createAndDownloadZip(zip, filename)

      if (failed.length > 0) {
        toast({
          title: t('downloadPartial'),
          description: t('downloadPartialDescription', {
            successCount,
            totalCount: documents.length,
            failedCount: failed.length,
            failedNames: failed.join(', ')
          }),
          variant: 'destructive'
        })
      } else {
        toast({
          title: t('success'),
          description: t('originalPdfsDownloaded'),
          variant: 'default'
        })
      }
    } catch (error) {
      console.error('Error downloading original PDFs:', error)
      throw error
    }
  }

  /**
   * Download organized PDFs (one per document group)
   */
  const downloadOrganizedPdfs = async (
    organizedDocuments: DocumentGroup[],
    pageRotations?: Map<string, number> | Record<string, number>
  ) => {
    try {
      const loadingToast = toast({
        title: t('downloading'),
        description: t('downloadingOrganizedPdfs'),
        variant: 'default'
      })

      const JSZip = (await import('jszip')).default
      const zip = new JSZip()
      const usedNames = new Set<string>()
      const failed: string[] = []

      for (const documentGroup of organizedDocuments) {
        const desiredName = `${documentGroup.name}.pdf`
        try {
          const pdfBytes = await createPdfFromPages(documentGroup.pages, pageRotations)
          zip.file(uniqueZipFilename(desiredName, usedNames), pdfBytes)
        } catch (error) {
          console.error(`Error processing document group ${documentGroup.name}:`, error)
          failed.push(desiredName)
        }
      }

      const successCount = organizedDocuments.length - failed.length
      loadingToast.dismiss()

      if (successCount === 0) {
        toast({
          title: t('downloadAllFailed'),
          description: t('downloadAllFailedDescription', {
            totalCount: organizedDocuments.length
          }),
          variant: 'destructive'
        })
        return
      }

      const filename = `organized_documents_${new Date().toISOString().split('T')[0]}.zip`
      await createAndDownloadZip(zip, filename)

      if (failed.length > 0) {
        toast({
          title: t('downloadPartial'),
          description: t('downloadPartialDescription', {
            successCount,
            totalCount: organizedDocuments.length,
            failedCount: failed.length,
            failedNames: failed.join(', ')
          }),
          variant: 'destructive'
        })
      } else {
        toast({
          title: t('success'),
          description: t('organizedPdfsDownloaded'),
          variant: 'default'
        })
      }
    } catch (error) {
      console.error('Error downloading organized PDFs:', error)
      throw error
    }
  }

  /**
   * Download a specific page as a PDF
   */
  const downloadSpecificPage = async (
    page: PageItem,
    documentGroupName: string,
    pageRotations?: Map<string, number> | Record<string, number>
  ) => {
    try {
      if (!page.url) {
        throw new Error('No document URL available')
      }

      const pageNumber = page.pageNumber || 1
      const filename = `${documentGroupName}_page_${pageNumber}.pdf`

      const pdfBytes = await createPdfFromPages([page], pageRotations)

      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast({
        title: t('success'),
        description: t('pageDownloaded', { pageName: filename }),
        variant: 'default'
      })
    } catch (error) {
      console.error('Error downloading page:', error)
      toast({
        title: t('error'),
        description: t('downloadError'),
        variant: 'destructive'
      })
      throw error
    }
  }

  /**
   * Generate PDF from a document group for saving
   */
  const generatePDFFromDocumentGroup = async (
    documentGroup: DocumentGroup,
    pageRotations?: Map<string, number> | Record<string, number>
  ): Promise<string> => {
    try {
      // Validate that the document group has pages
      if (!documentGroup.pages || documentGroup.pages.length === 0) {
        throw new Error(`Document group ${documentGroup.name} has no pages`)
      }

      // Safety check: prevent processing too many pages (avoid infinite loops)
      if (documentGroup.pages.length > 3000) {
        throw new Error(
          `Document group ${documentGroup.name} has too many pages (${documentGroup.pages.length}), maximum allowed is 3000`
        )
      }

      // Validate that pages have valid URLs
      const invalidPages = documentGroup.pages.filter((page) => !page.url)
      if (invalidPages.length > 0) {
        throw new Error(
          `Document group ${documentGroup.name} has ${invalidPages.length} pages without valid URLs`
        )
      }

      // Use the existing function for PDF generation
      const pdfBytes = await createPdfFromPages(documentGroup.pages, pageRotations)

      const blob = new Blob([pdfBytes], { type: 'application/pdf' })

      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result as string
          const base64String = result.split(',')[1]
          resolve(base64String)
        }
        reader.onerror = () => reject(new Error('Failed to read PDF blob'))
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('Error generating PDF for document group:', documentGroup.name, error)
      throw new Error(`Failed to generate PDF for ${documentGroup.name}: ${error}`)
    }
  }

  /**
   * Create a ZIP file from multiple PDFs
   */
  const createZipFromPdfs = async (
    pdfs: Array<{ filename: string; blob: Blob }>,
    zipName: string
  ) => {
    try {
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()
      const usedNames = new Set<string>()

      for (const pdf of pdfs) {
        zip.file(uniqueZipFilename(pdf.filename, usedNames), pdf.blob)
      }

      await createAndDownloadZip(zip, zipName)
      return zip
    } catch (error) {
      console.error('Error creating ZIP file:', error)
      throw error
    }
  }

  return {
    // Core PDF operations
    createPdfFromPages,
    generatePDFFromDocumentGroup,
    createDocumentPages,
    validateDocumentPageCount,
    getActualPageCount,

    // Download operations
    downloadOriginalPdfs,
    downloadOrganizedPdfs,
    downloadSpecificPage,
    createZipFromPdfs,

    // Utility functions
    fetchDocumentAsBlob,
    fetchDocumentAsArrayBuffer,
    createAndDownloadZip
  }
}
